/**
 * Sending a long upload in batches.
 *
 * A full product line is roughly 10,000 mapping rows — 34 parents × 148 colours × 2 pieces. That
 * is over Fastify's 1 MB body limit AND over the 5,000-row cap, and ten thousand sequential
 * inserts in one request invites a timeout on top. Raising all three limits would only move the
 * wall; batching removes it, and keeps each request small enough to fail cheaply.
 *
 * Sequential on purpose: the batches write to the same table, so overlapping them buys nothing and
 * makes a partial failure harder to reason about. Each batch gets its starting offset so a
 * rejection numbered within a batch can be renumbered to the line the user actually pasted.
 */
export async function inChunks<T, R>(
  items: T[],
  size: number,
  run: (batch: T[], offset: number) => Promise<R>,
  onProgress?: (done: number, total: number) => void,
): Promise<R[]> {
  const out: R[] = [];
  for (let i = 0; i < items.length; i += size) {
    const batch = items.slice(i, i + size);
    // Not caught: a later batch cannot repair an earlier failure, and pressing on would make the
    // report claim more was written than actually was.
    out.push(await run(batch, i));
    onProgress?.(Math.min(i + size, items.length), items.length);
  }
  return out;
}
